import * as React from 'react';
import {
    Alert, Box, Button, Card, CardContent, CircularProgress, FormControl, FormControlLabel, InputLabel,
    MenuItem, Pagination, Select, Snackbar, Stack, Switch, TextField, Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { generateVirtualPlayer, getVirtualCatalogSports, searchVirtualProviderPlayers } from '../Api';
import VirtualCatalogAddMenu from '../components/virtual-sports/VirtualCatalogAddMenu';
import VirtualImmediateGenerationDialog from '../components/virtual-sports/VirtualImmediateGenerationDialog';
import VirtualPlayerCreationDialog from '../components/virtual-sports/VirtualPlayerCreationDialog';
import VirtualPlayersTable from '../components/virtual-sports/VirtualPlayersTable';
import VirtualActivityLogBell from '../components/virtual-sports/VirtualActivityLogBell';
import VirtualPlayerDetailsDialog from '../components/virtual-sports/VirtualPlayerDetailsDialog';
import VirtualPlayerImportDialog from '../components/virtual-sports/VirtualPlayerImportDialog';
import VirtualCountrySelect from '../components/virtual-sports/VirtualCountrySelect';
import VirtualTeamSearchSelect from '../components/virtual-sports/VirtualTeamSearchSelect';
import {
    VirtualPlayerTeamFilter, VirtualProviderPlayerDto, VirtualProviderTeamDto,
} from '../models/dtos/VirtualProviderCatalogDto';
import { VirtualCatalogSportDto } from '../models/dtos/VirtualSportsCatalogDto';

const PAGE_SIZE = 25;

export default function VirtualSportsPlayersPage() {
    const navigate = useNavigate();
    const [page, setPage] = React.useState(0);
    const [players, setPlayers] = React.useState<VirtualProviderPlayerDto[]>([]);
    const [sports, setSports] = React.useState<VirtualCatalogSportDto[]>([]);
    const [totalItems, setTotalItems] = React.useState(0);
    const [searchTerm, setSearchTerm] = React.useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = React.useState('');
    const [originCountry, setOriginCountry] = React.useState('');
    const [teamFilterEnabled, setTeamFilterEnabled] = React.useState(false);
    const [teamFilter, setTeamFilter] = React.useState<VirtualPlayerTeamFilter>('ANY_TEAM');
    const [specificTeam, setSpecificTeam] = React.useState<VirtualProviderTeamDto | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [manualOpen, setManualOpen] = React.useState(false);
    const [immediateOpen, setImmediateOpen] = React.useState(false);
    const [importOpen, setImportOpen] = React.useState(false);
    const [reloadKey, setReloadKey] = React.useState(0);
    const [error, setError] = React.useState('');
    const [notice, setNotice] = React.useState('');
    const [selectedPlayer, setSelectedPlayer] = React.useState<VirtualProviderPlayerDto | null>(null);

    React.useEffect(() => {
        getVirtualCatalogSports().then(setSports).catch(reason =>
            setError(reason instanceof Error ? reason.message : 'Could not load virtual sports'));
    }, []);

    React.useEffect(() => {
        const timeout = window.setTimeout(() => {
            setDebouncedSearchTerm(searchTerm.trim());
            setPage(0);
        }, 300);
        return () => window.clearTimeout(timeout);
    }, [searchTerm]);

    React.useEffect(() => {
        if (teamFilterEnabled && teamFilter === 'SPECIFIC_TEAM' && !specificTeam) {
            setPlayers([]);
            setTotalItems(0);
            setLoading(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        setError('');
        searchVirtualProviderPlayers({
            offset: page * PAGE_SIZE,
            limit: PAGE_SIZE,
            searchTerm: debouncedSearchTerm || undefined,
            originCountry: originCountry || undefined,
            teamFilter: teamFilterEnabled ? teamFilter : undefined,
            teamId: teamFilterEnabled && teamFilter === 'SPECIFIC_TEAM' ? specificTeam?.id : undefined,
        }).then(result => {
            if (cancelled) return;
            setPlayers(result.items);
            setTotalItems(result.totalItems);
        }).catch(reason => {
            if (!cancelled) setError(reason instanceof Error ? reason.message : 'Could not load virtual players');
        }).finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [page, debouncedSearchTerm, originCountry, teamFilterEnabled, teamFilter, specificTeam, reloadKey]);

    const reloadFirstPage = () => {
        if (page === 0) setReloadKey(value => value + 1);
        else setPage(0);
    };
    const playerCreated = (player: VirtualProviderPlayerDto) => {
        setNotice(`${player.name} was created.`);
        reloadFirstPage();
    };
    const playerUpdated = (player: VirtualProviderPlayerDto) => {
        setSelectedPlayer(player);
        setNotice(`${player.name} was updated.`);
        setReloadKey(value => value + 1);
    };
    const playerDeleted = (_playerId: string, playerName: string) => {
        setSelectedPlayer(null);
        setNotice(`${playerName} was deleted and removed from all teams.`);
        if (players.length === 1 && page > 0) setPage(current => current - 1);
        else setReloadKey(value => value + 1);
    };
    const sportNames = new Map(sports.map(sport => [sport.id, sport.name]));

    return <Stack spacing={3} sx={{ mx: { xs: 2, md: 5 }, my: 5 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <Box sx={{ flex: 1 }}>
                <Typography variant="h3">Virtual players</Typography>
                <Typography color="text.secondary">{totalItems.toLocaleString()} players found</Typography>
            </Box>
            <VirtualCatalogAddMenu
                entityLabel="player"
                onAdd={() => setManualOpen(true)}
                onGenerateImmediately={() => setImmediateOpen(true)}
                onImportFromFile={() => setImportOpen(true)}
            />
            <VirtualActivityLogBell />
            <Button variant="outlined" onClick={() => navigate('/virtual-sports')}>Back to overview</Button>
        </Stack>

        {error ? <Alert severity="error">{error}</Alert> : null}
        <Stack spacing={2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                    fullWidth
                    label="Search by player name or biography"
                    type="search"
                    value={searchTerm}
                    onChange={event => setSearchTerm(event.target.value)}
                />
                <VirtualCountrySelect
                    value={originCountry}
                    onChange={value => { setOriginCountry(value); setPage(0); }}
                    required={false}
                    label="Country filter"
                />
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                <FormControlLabel
                    control={<Switch checked={teamFilterEnabled} onChange={event => {
                        setTeamFilterEnabled(event.target.checked);
                        setPage(0);
                    }} />}
                    label="Team filter"
                />
                <FormControl sx={{ minWidth: 190 }} disabled={!teamFilterEnabled}>
                    <InputLabel>Team assignment</InputLabel>
                    <Select
                        label="Team assignment"
                        value={teamFilter}
                        onChange={event => {
                            const value = event.target.value as VirtualPlayerTeamFilter;
                            setTeamFilter(value);
                            if (value !== 'SPECIFIC_TEAM') setSpecificTeam(null);
                            setPage(0);
                        }}
                    >
                        <MenuItem value="ANY_TEAM">Any team</MenuItem>
                        <MenuItem value="NO_TEAM">No team</MenuItem>
                        <MenuItem value="SPECIFIC_TEAM">Specific team</MenuItem>
                    </Select>
                </FormControl>
                <VirtualTeamSearchSelect
                    value={specificTeam}
                    onChange={value => { setSpecificTeam(value); setPage(0); }}
                    disabled={!teamFilterEnabled || teamFilter !== 'SPECIFIC_TEAM'}
                    label="Specific team"
                />
            </Stack>
        </Stack>

        <Card variant="outlined"><CardContent>
            {loading ? <Box textAlign="center"><CircularProgress /></Box> : (
                <VirtualPlayersTable players={players} sportNames={sportNames} onPlayerClick={setSelectedPlayer} />
            )}
        </CardContent></Card>

        {totalItems > PAGE_SIZE ? <Pagination
            count={Math.ceil(totalItems / PAGE_SIZE)}
            page={page + 1}
            onChange={(_, value) => setPage(value - 1)}
            sx={{ alignSelf: 'center' }}
        /> : null}

        <VirtualPlayerCreationDialog open={manualOpen} sports={sports} onClose={() => setManualOpen(false)} onCreated={playerCreated} />
        <VirtualImmediateGenerationDialog
            open={immediateOpen}
            entityLabel="player"
            sports={sports}
            onClose={() => setImmediateOpen(false)}
            onGenerate={async (sportId, count, onProgress, options) => {
                const generatedPlayers: VirtualProviderPlayerDto[] = [];
                try {
                    for (let index = 0; index < count; index += 1) {
                        generatedPlayers.push(await generateVirtualPlayer(sportId, options.generatePortraits));
                        onProgress(generatedPlayers.length);
                    }
                } finally {
                    if (generatedPlayers.length > 0) {
                        setNotice(generatedPlayers.length === 1
                            ? `${generatedPlayers[0].name} was created.`
                            : `${generatedPlayers.length} players were created.`);
                        reloadFirstPage();
                    }
                }
            }}
        />
        <VirtualPlayerImportDialog
            open={importOpen}
            onClose={() => setImportOpen(false)}
            onImported={result => {
                setNotice(`${result.importedCount} ${result.importedCount === 1 ? 'player was' : 'players were'} imported.`);
                reloadFirstPage();
            }}
        />
        <VirtualPlayerDetailsDialog
            player={selectedPlayer}
            sportNames={sportNames}
            onClose={() => setSelectedPlayer(null)}
            onUpdated={playerUpdated}
            onDeleted={playerDeleted}
        />
        <Snackbar open={Boolean(notice)} autoHideDuration={5000} onClose={() => setNotice('')} message={notice} />
    </Stack>;
}
